import type {
  GpoRequestStatus,
  GuestCreatorInfo,
  InquirySource,
  QuotePriceUnit,
  RestaurantInquiry,
  RestaurantInquiryItem,
} from 'apis/restaurant-inquiries';
import { RestaurantInquiryItemStatus } from 'apis/restaurant-inquiries';
import type { PriceTier, UomLadder } from 'utils/price-units';
import { availableTiers, convertPrice, tierUnit, TIER_ORDER } from 'utils/price-units';

export const isAppInquiry = (inquiry?: { source?: InquirySource } | null) => inquiry?.source === 'APP';

// A signed-out visitor's request is filed under an inactive guest account; `creator.guest` is set until that
// guest activates (then null) and absent on an older backend.
export const isGuestInquiry = (inquiry?: { creator?: { guest?: GuestCreatorInfo | null } | null } | null): boolean =>
  inquiry?.creator?.guest != null;

export const requestedByText = (inquiry: RestaurantInquiry): string => {
  const { creator } = inquiry;
  const guest = creator?.guest;
  if (guest) return [creator?.name, guest.contactPhoneNumber, guest.contactEmail].filter(Boolean).join(' · ');
  return [creator?.name, creator?.phoneNumber].filter(Boolean).join(' · ') || 'Restaurant user';
};

export const guestAddressesText = (inquiry: RestaurantInquiry): string | null => {
  const addresses = inquiry.creator?.guest?.addresses;
  return addresses?.length ? addresses.join(' | ') : null;
};

// gpoOrderId is the usual signal, but a freshly-created app item can carry the
// live gpoRequest before gpoOrderId is backfilled on the row — either is proof.
export const isAppItem = (item?: { gpoOrderId?: number | null; gpoRequest?: unknown | null } | null) =>
  item?.gpoOrderId != null || item?.gpoRequest != null;

// Labels are the ones the restaurant sees in the app, so ops and the customer use the same words.
export const APP_REQUEST_STATE_META: Record<GpoRequestStatus, { label: string; fg: string; bg: string }> = {
  pending: { label: 'Awaiting price', fg: '#b45309', bg: '#fef3c7' },
  confirmed: { label: 'Price confirmed', fg: '#047857', bg: '#d1fae5' },
  placed: { label: 'Ordered', fg: '#1d4ed8', bg: '#dbeafe' },
  cancelled: { label: 'Cancelled', fg: '#6b7280', bg: '#eef1f6' },
  expired: { label: 'Expired', fg: '#b91c1c', bg: '#fee2e2' },
};

const MAX_CONFIRMED_UNIT_PRICE = 9_999_999.99;
const HOUR_IN_MS = 60 * 60 * 1000;

// Render-safe money formatting for gpoRequest/awardPreview values, which can
// legitimately be null (no preview yet) or arrive malformed from the API —
// never let a bad value throw mid-render. Number(null) is 0 (finite), so null
// is excluded explicitly rather than relying on Number.isFinite alone.
export const formatMoney2 = (n: unknown): string =>
  n != null && Number.isFinite(Number(n)) ? Number(n).toFixed(2) : '—';

/**
 * The rung an app request is quoted on — the mirror of the BE's `deriveStoredQuote`: the request's
 * stored unit word names BASE, else exactly ONE outer rung of the item's master ladder as it reads
 * now. Null when the word names no rung, or two (an item and a pack both called 'carton'): the BE
 * then refuses the award (422) rather than guess.
 */
export function requestTierOf(requestUnit: string | null | undefined, ladder: UomLadder): PriceTier | null {
  const word = requestUnit?.trim().toLowerCase() ?? '';
  if (!word) return null;
  const names = (tier: PriceTier) => tierUnit(ladder, tier)?.toLowerCase() === word;
  if (names('BASE')) return 'BASE';
  const outer = TIER_ORDER.filter((tier) => tier !== 'BASE' && names(tier));
  return outer.length === 1 ? outer[0] : null;
}

/**
 * What the restaurant will be shown if this quote is awarded: the quote price restated per the
 * REQUEST's unit, through the ITEM's master ladder — the same two inputs the BE award uses
 * (`quoteToTierPrice`). Mirrors it rule for rule: the quote sits on its stored rung when the ladder
 * still defines it, else the OUTERMOST defined rung (NOT resolveTier's defaultUnit fallback: the BE
 * does not apply it, and a preview that disagrees with the push is worse than none), and is converted
 * to the rung the request's stored unit word names. Null = the BE will refuse the award (422).
 *
 * The ladder is the ITEM's master product, never the quote's `product`: that is the ladder the BE
 * converts through, and a quote since the products freeze has no products row of its own.
 */
export function previewRequestUnitPrice(
  quote: { price: number | string; priceUnit?: QuotePriceUnit | null },
  ladder: UomLadder,
  requestUnit: string | null | undefined
): number | null {
  const tiers = availableTiers(ladder);
  if (!tiers.length) return null;
  const quoteTier =
    quote.priceUnit && tiers.some((entry) => entry.tier === quote.priceUnit) ? quote.priceUnit : tiers[0].tier;
  const targetTier = requestTierOf(requestUnit, ladder);
  if (!targetTier) return null;

  const converted = convertPrice(Number(quote.price), quoteTier, targetTier, ladder);
  if (converted == null || !Number.isFinite(converted)) return null;

  const rounded = Math.round((converted + Number.EPSILON) * 100) / 100;
  return rounded > 0 && rounded <= MAX_CONFIRMED_UNIT_PRICE ? rounded : null;
}

export function describeExpiry(expiresAt: string | null, now: number): { text: string; urgent: boolean } | null {
  if (!expiresAt) return null;
  const remaining = new Date(expiresAt).getTime() - now;
  // null stays reserved for "no expiry to show" (absent expiresAt) — once the
  // window has actually lapsed the pill should say so, not fall silent.
  if (remaining <= 0) return { text: 'expired', urgent: true };
  return remaining < HOUR_IN_MS
    ? { text: 'expires soon', urgent: true }
    : { text: `valid for ${Math.floor(remaining / HOUR_IN_MS)}h`, urgent: false };
}

export const canDeclineAppItem = (item: RestaurantInquiryItem) =>
  isAppItem(item) &&
  item.status !== RestaurantInquiryItemStatus.FULFILLED &&
  item.status !== RestaurantInquiryItemStatus.UNFULFILLED;

/**
 * Null = awardable as far as the FE can tell. The BE re-checks under a row lock.
 * quoteMasterId is the offered quote's product's master id (via its
 * masterProduct link) — the BE 422s (QUOTE_PRODUCT_MISMATCH) when it differs
 * from the item's own masterProductId, so block it here first with a
 * readable reason instead of surfacing the raw 422.
 */
export function awardBlockReason(
  item: RestaurantInquiryItem,
  preview: number | null,
  quoteMasterId?: number | null
): string | null {
  if (!isAppItem(item)) return null;
  const state = item.gpoRequest?.status;
  if (state === 'cancelled') return 'The restaurant cancelled this request in the app.';
  if (state === 'placed') return 'The restaurant already placed this order in the app.';
  if (state === 'confirmed') return 'This request already has a confirmed price — revert that award first.';
  if (quoteMasterId != null && item.masterProductId != null && quoteMasterId !== item.masterProductId) {
    return 'This quote is for a different product than the one the restaurant requested in the app.';
  }
  if (preview == null) {
    const unit = item.gpoRequest?.unit?.trim() || item.masterProduct?.baseUnit || 'base unit';
    return `This quote cannot be converted to a price per ${unit}. Check that the product still lists ${unit} as a packaging unit, and set the price unit on the quote (and the pack size / net content on the product).`;
  }
  return null;
}
